import React, { useMemo, useState } from "react";
import { client } from "../../api/client";
import useAuthStore from "../../store/useAuthStore";
import RoleBadge from "./components/RoleBadge";
import StatCard from "./components/StatCard";
import StatusBadge from "./components/StatusBadge";
import ResetPasswordModal from "./components/ResetPasswordModal";
import CreateUserModal from "./components/CreateUserModal";
import {
  bulkActionLabel,
  extractApiError,
  formatDate,
  getInitial,
  skipReasonLabel,
} from "./helpers";
import useAdminUsers from "./hooks/useAdminUsers";
import useDebouncedValue from "./hooks/useDebouncedValue";
import { AdminUser, BulkAction, BulkActionResponse } from "./types";

const PAGE_SIZE = 20;

interface Props {
  showConfirm: (config: {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    variant?: "primary" | "danger" | "warning";
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  showInfo: (config: {
    title: string;
    message: React.ReactNode;
    variant?: "success" | "info" | "warning";
  }) => void;
}

const UsersTab: React.FC<Props> = ({ showConfirm, showInfo }) => {
  const currentUser = useAuthStore((s) => s.user);

  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim(), 250);

  const {
    users,
    count,
    stats,
    page,
    setPage,
    isLoading,
    error,
    reload,
    reloadStats,
    patchLocal,
  } = useAdminUsers(search);

  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(
    null
  );
  const [busyId, setBusyId] = useState<number | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const disabledRow = (u: AdminUser): string | null => {
    if (currentUser?.id === u.id) return "your own account";
    if (u.is_superuser) return "superuser";
    return null;
  };

  const selectableUsers = useMemo(
    () => users.filter((u) => !disabledRow(u)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, currentUser?.id]
  );

  const selectedCount = selectedIds.size;
  const selectedOnPage = selectableUsers.filter((u) => selectedIds.has(u.id))
    .length;
  const allChecked =
    selectableUsers.length > 0 && selectedOnPage === selectableUsers.length;
  const someChecked = selectedOnPage > 0 && !allChecked;

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectable = selectableUsers.map((u) => u.id);
    const allSelected = selectable.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) selectable.forEach((id) => next.delete(id));
      else selectable.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const runUpdate = async (target: AdminUser, patch: Partial<AdminUser>) => {
    setBusyId(target.id);
    setRowError(null);
    try {
      const resp = await client.patch<AdminUser>(
        `/auth/admin/users/${target.id}/`,
        patch
      );
      patchLocal(resp.data);
      reloadStats();
    } catch (err) {
      setRowError({
        id: target.id,
        message: extractApiError(err, "Update failed."),
      });
    } finally {
      setBusyId(null);
    }
  };

  const toggleRole = (target: AdminUser) => {
    const nextRole = target.role === "admin" ? "viewer" : "admin";
    const isPromotion = nextRole === "admin";
    showConfirm({
      title: isPromotion ? "Promote to admin" : "Demote to viewer",
      message: `Change role for ${target.email} to "${nextRole}"?`,
      confirmLabel: isPromotion ? "Promote" : "Demote",
      variant: isPromotion ? "primary" : "danger",
      onConfirm: () => runUpdate(target, { role: nextRole }),
    });
  };

  const toggleActive = (target: AdminUser) => {
    const nextActive = !target.is_active;
    const action = nextActive ? "Activate" : "Deactivate";
    showConfirm({
      title: `${action} account`,
      message: nextActive
        ? `Reactivate ${target.email}? The user will be able to log in again.`
        : `Deactivate ${target.email}? Active sessions will be signed out immediately and the user will not be able to log in.`,
      confirmLabel: action,
      variant: nextActive ? "primary" : "danger",
      onConfirm: () => runUpdate(target, { is_active: nextActive }),
    });
  };

  const runBulk = async (action: BulkAction) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkSubmitting(true);
    try {
      const resp = await client.post<BulkActionResponse>(
        "/auth/admin/users/bulk/",
        { ids, action }
      );
      await reload();
      reloadStats();
      clearSelection();

      const skippedMsg =
        resp.data.skipped.length > 0
          ? "\nSkipped:\n" +
            resp.data.skipped
              .map((s) => `• ID ${s.id}: ${skipReasonLabel(s.reason)}`)
              .join("\n")
          : "";

      showInfo({
        title: `${bulkActionLabel(action)} — done`,
        message: `${resp.data.updated.length} user(s) updated.${skippedMsg}`,
        variant: resp.data.skipped.length > 0 ? "warning" : "success",
      });
    } catch (err) {
      showInfo({
        title: "Bulk action failed",
        message: extractApiError(err, "Bulk action failed."),
        variant: "warning",
      });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const confirmBulk = (action: BulkAction) => {
    const isDestructive = action === "demote_viewer" || action === "deactivate";
    showConfirm({
      title: bulkActionLabel(action),
      message: `Apply "${bulkActionLabel(action).toLowerCase()}" to ${selectedCount} user${selectedCount === 1 ? "" : "s"}? Superusers and your own account will be skipped automatically.`,
      confirmLabel: bulkActionLabel(action),
      variant: isDestructive ? "danger" : "primary",
      onConfirm: () => runBulk(action),
    });
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1 grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard label="Total users" value={stats?.total ?? count} tone="blue" />
          <StatCard label="Admins" value={stats?.admin_count ?? 0} tone="purple" />
          <StatCard
            label="Inactive"
            value={stats?.inactive_count ?? 0}
            tone="gray"
          />
        </div>
        <div className="pl-4">
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            + Create user
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z"
              />
            </svg>
          </div>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email — filter updates as you type"
            className="w-full pl-10 pr-10 p-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        {search && (
          <p className="mt-2 text-xs text-gray-500">
            Showing results for{" "}
            <span className="font-medium text-gray-700">{search}</span>
          </p>
        )}
      </div>

      {error && (
        <div
          className="p-4 mb-4 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
          role="alert"
        >
          {error}
        </div>
      )}

      {selectedCount > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 p-3 mb-3 text-sm bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <span className="font-medium text-blue-900">
              {selectedCount} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-blue-700 hover:underline"
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={bulkSubmitting}
              onClick={() => confirmBulk("promote_admin")}
              className="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Promote to admin
            </button>
            <button
              type="button"
              disabled={bulkSubmitting}
              onClick={() => confirmBulk("demote_viewer")}
              className="px-3 py-1 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              Demote to viewer
            </button>
            <button
              type="button"
              disabled={bulkSubmitting}
              onClick={() => confirmBulk("activate")}
              className="px-3 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
            >
              Activate
            </button>
            <button
              type="button"
              disabled={bulkSubmitting}
              onClick={() => confirmBulk("deactivate")}
              className="px-3 py-1 text-xs text-white bg-gray-700 rounded hover:bg-gray-800 disabled:opacity-50"
            >
              Deactivate
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left text-gray-700">
            <thead className="text-xs uppercase text-gray-500 bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => {
                      if (el) el.indeterminate = someChecked;
                    }}
                    onChange={toggleSelectAll}
                    aria-label="Select all rows on this page"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3">Last login</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                    {search ? `No users match "${search}".` : "No users yet."}
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const blockedReason = disabledRow(u);
                  const isBusy = busyId === u.id;
                  const isChecked = selectedIds.has(u.id);
                  return (
                    <React.Fragment key={u.id}>
                      <tr
                        className={`border-t border-gray-100 transition-colors ${
                          isChecked ? "bg-blue-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 align-middle">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={!!blockedReason}
                            onChange={() => toggleSelected(u.id)}
                            aria-label={`Select ${u.email}`}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-40"
                          />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-9 h-9 text-sm font-medium text-blue-700 bg-blue-100 rounded-full">
                              {getInitial(u.email)}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {u.email}
                              </div>
                              {u.is_superuser && (
                                <span className="inline-block px-1.5 py-0.5 mt-0.5 text-[10px] font-medium text-indigo-700 bg-indigo-100 rounded">
                                  superuser
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <RoleBadge role={u.role} />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <StatusBadge active={u.is_active} />
                        </td>
                        <td className="px-4 py-3 align-middle text-gray-600">
                          {formatDate(u.date_joined)}
                        </td>
                        <td className="px-4 py-3 align-middle text-gray-600">
                          {u.last_login ? formatDate(u.last_login) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="inline-flex gap-1">
                            <button
                              type="button"
                              disabled={!!blockedReason || isBusy}
                              onClick={() => toggleRole(u)}
                              className="px-2 py-1 text-xs text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              title={
                                blockedReason
                                  ? `Cannot modify ${blockedReason}`
                                  : u.role === "admin"
                                    ? "Demote to user"
                                    : "Promote to admin"
                              }
                            >
                              {u.role === "admin" ? "Demote" : "Promote"}
                            </button>
                            <button
                              type="button"
                              disabled={!!blockedReason || isBusy}
                              onClick={() => toggleActive(u)}
                              className={`px-2 py-1 text-xs border rounded disabled:opacity-40 disabled:cursor-not-allowed ${
                                u.is_active
                                  ? "text-gray-700 border-gray-300 hover:bg-gray-50"
                                  : "text-green-700 border-green-200 hover:bg-green-50"
                              }`}
                              title={
                                blockedReason
                                  ? `Cannot modify ${blockedReason}`
                                  : u.is_active
                                    ? "Deactivate account"
                                    : "Activate account"
                              }
                            >
                              {u.is_active ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              type="button"
                              disabled={!!blockedReason || isBusy}
                              onClick={() => setResetTarget(u)}
                              className="px-2 py-1 text-xs text-orange-700 bg-white border border-orange-200 rounded hover:bg-orange-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              title={
                                blockedReason
                                  ? `Cannot modify ${blockedReason}`
                                  : "Reset password"
                              }
                            >
                              Reset PW
                            </button>
                          </div>
                        </td>
                      </tr>
                      {rowError?.id === u.id && (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-2 text-sm text-red-700 bg-red-50"
                          >
                            {rowError.message}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
        <span>
          {count} user{count === 1 ? "" : "s"} total
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Prev
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      <ResetPasswordModal
        target={resetTarget}
        onClose={() => setResetTarget(null)}
        onSuccess={(email) => {
          setResetTarget(null);
          showInfo({
            title: "Password reset",
            message: `The temporary password for ${email} has been set. Share it with the user securely so they can sign in and change it.`,
            variant: "success",
          });
        }}
      />

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(user) => {
            setShowCreateModal(false);
            reload();
            reloadStats();
            showInfo({
              title: "User created",
              message: `Successfully created ${user.email} as an active ${user.role}.`,
              variant: "success",
            });
          }}
        />
      )}
    </>
  );
};

export default UsersTab;
